import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../helper/useAuth";
import { httpService } from "../../services/httpService";
import { API_CONFIG } from "../../shared/constants";
import { AdmobApp, App } from "../../features/AppCountryReports/interface";
import dayjs from "dayjs";

interface CountryReportsResponse {
  current_page: number;
  from: number;
  last_page: number;
  per_page: number;
  to: number;
  total: number;
  results: {
    admob_apps: AdmobApp[];
    apps: App[];
    average_actual_usd: string;
  };
}

interface CountryReportsParams {
  startDate: Date;
  endDate: Date;
  delay?: number;
}

export const useCountryReports = (params: CountryReportsParams) => {
  const { selectedIntegration } = useAuth();
  const { startDate, endDate } = params;

  // Get today's date at midnight for cache key
  const today = dayjs().startOf("day").format("YYYY-MM-DD");

  const from = dayjs(startDate).format("YYYY-MM-DD");
  const to = dayjs(endDate).format("YYYY-MM-DD");

  // Helper function to add delay
  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  // Batching logic: fetch all pages in batches of 2000
  return useQuery({
    queryKey: ["countryReports", selectedIntegration?.value, from, to, today],
    queryFn: async () => {
      const perPage = 2000;
      let page = 1;
      let allAdmobApps: AdmobApp[] = [];
      let allApps: App[] = [];
      let average_actual_usd: string = "";
      let lastPage = 1;
      let isFirst = true;

      do {
        const response = await httpService.get<CountryReportsResponse>(
          API_CONFIG.path.countryReports,
          {
            page,
            per_page: perPage,
            from,
            to,
            tracking_id: selectedIntegration?.value as string,
          }
        );
        // ... results logic etc
        if (isFirst) {
          average_actual_usd = response.results.average_actual_usd;
          isFirst = false;
          lastPage = response.last_page;
        }
        allAdmobApps = allAdmobApps.concat(response.results.admob_apps);
        allApps = allApps.concat(response.results.apps);
        page++;
        // Add 500ms delay between API calls, except after the last call
        if (page <= lastPage) {
          await sleep(params.delay || 100);
        }
      } while (page <= lastPage);

      // Remove duplicate apps by app_slug (if any)
      const uniqueApps = Array.from(
        new Map(allApps.map((app) => [app.app_slug, app])).values()
      );

      return {
        admob_apps: allAdmobApps,
        apps: uniqueApps,
        average_actual_usd,
      };
    },
    // Only fetch if we have a selected integration
    enabled: !!selectedIntegration?.value,
    // Cache for 1 hours
    staleTime: 1 * 60 * 60 * 1000,
    gcTime: 1 * 60 * 60 * 1000,
  });
};
